import Vue from "vue";
import { ICalendarDayConfig, getCalendarEntries } from "js/crossword/calendarData";
import { ILocalizedDate } from "js/framework/Localization/getLocalizationProvider";
import { Provider } from "js/crossword/types";
import { clamp } from "framework/utils/math-utils";
import { getAverageTimeForPuzzles } from "js/crossword/completeTime";
import { getMonthsList, IMonthListItem } from "js/crossword/getYearsInfo";
import { trackPickDateCalendarPlay } from "js/tracking/track";
import OS from "framework/utils/OS";
import CalendarDay from "./CalendarDay";
import CalendarMonthPicker from "./CalendarMonthPicker";
import CalendarProgressPanel from "./CalendarProgressPanel";
import PopupButtons from "./PopupButtons";
import PopupContainer from "./PopupContainer";

import styles from "../css/CalendarPopup.module.css";
declare const sdkHandler: any;

function createDummyDays() {
  const result: ICalendarDayConfig[] = [];
  for (let i = 0; i < 6 * 7; i++) {
    result.push({
      dayNumber: Math.floor(Math.random() * 100),
      fillRate: Math.random(),
      puzzleUrl: null,
      currentDay: Math.random() < 0.05,
      currentMonth: false,
      completed: false,
      author: null,
      date: null,
      puzzleId: null,
    });
  }
  return result;
}

interface ICalendarPopUpData {
  desktop: boolean;
  availableMonths: IMonthListItem[] | null;
  currentMonthIndex: number;
  loading: boolean;
  progress: {
    completed: number;
    averageSolveRate: number;
    averageTime: number;
  };
  dayEntries: ICalendarDayConfig[];
  selectedDayIndex: number | null;
}

export default Vue.extend({
  inject: [Provider.LOCALIZATION],
  props: {
    puzzleId: String,
    puzzleDate: String
  },
  data(): ICalendarPopUpData {
    return {
      desktop: OS.desktop,
      loading: true,
      availableMonths: null,
      currentMonthIndex: -1,
      progress: {
        completed: 0,
        averageSolveRate: 0,
        averageTime: 0,
      },
      dayEntries: createDummyDays(),
      selectedDayIndex: 15,
    };
  },
  computed: {
    currentDay(): ICalendarDayConfig | null {
      if (this.selectedDayIndex === null) return null;
      const day = this.dayEntries[this.selectedDayIndex];
      if (!day) return null;
      if (!day.puzzleUrl) return null;
      return day;
    },
    localizedDate(): ILocalizedDate | null {
      const date = this.currentDay && this.currentDay.date;
      return date ? this.localization.getDate(date) : null;
    },
  },
  mounted() {
    getMonthsList().then((monthsList) => {
      this.availableMonths = monthsList;
      this.currentMonthIndex = monthsList.length - 1;
      const puzzleMonth = monthsList.findIndex((month) => month.availableDates.some(date => date === this.puzzleDate));
      this.currentMonthIndex = puzzleMonth;
    }); 
  },
  watch: {
    currentMonthIndex(newVal) {
      if (this.availableMonths === null) return;
      if (!this.availableMonths[this.currentMonthIndex]) return;

      this.loading = true;
      const { year, month } = this.availableMonths[this.currentMonthIndex];
      getCalendarEntries(year, month, this.puzzleId).then((monthData) => {
        if (this.availableMonths === null) return;
        if (this.currentMonthIndex !== newVal) return;
        this.dayEntries.forEach((dayEntry, index) => {
          Object.assign(dayEntry, monthData[index]);
        });

        this.selectedDayIndex = null;

        const currentDay = this.dayEntries.find((day) => day.puzzleId === this.puzzleId);
        if (currentDay) this.selectedDayIndex = this.dayEntries.indexOf(currentDay);

        this.progress.averageTime = getAverageTimeForPuzzles(
          monthData.filter((day) => day.puzzleId).map((day) => day.puzzleId) as string[],
        );

        const currentMonthDays = monthData.filter((days) => days.currentMonth);
        this.progress.completed = currentMonthDays.filter((day) => day.completed).length;

        const daysWithFillRate = currentMonthDays.filter((day) => day.fillRate !== 0);
        const totalFillRate = daysWithFillRate.reduce((acc, day) => {
          return acc + (day.fillRate || 0);
        }, 0) / daysWithFillRate.length;
        const solveRate = totalFillRate / daysWithFillRate.length;
        this.progress.averageSolveRate = isNaN(solveRate) ? 0 : solveRate;

        this.loading = false;
        // @ts-ignore
        this.$refs.container.onResize();
      });
    },
  },
  methods: {
    changeMonthIndex(indexDelta: number): void {
      if (this.availableMonths === null) return;
      this.currentMonthIndex = clamp(this.currentMonthIndex + indexDelta, 0, this.availableMonths.length - 1);
    },
    changeDayIndex(dayIndex: number): void {
      if (this.dayEntries[dayIndex] && !this.dayEntries[dayIndex].puzzleUrl) return;
      this.selectedDayIndex = dayIndex;
    },
    playHandler(): void {
      if (!this.currentDay) return;
      this.$emit("close");
      this.$emit("pickedDay", this.currentDay);
      trackPickDateCalendarPlay();
    },
    getAvailableMonth() {
      return this.availableMonths || null;
    }
  },
  render() {
    if (!this.availableMonths) return <div></div>

    const { weekdayName = "", ordinalDay = "" } = this.localizedDate || {};
    return (
      <PopupContainer
        customClasses={[styles.container]}
        handleClose={() => sdkHandler.trigger('interstitialAd', { callback: function() {
          this.$emit("close");
        }}, this)}
        ref="container"
      >
        <CalendarMonthPicker
          availableMonths={this.availableMonths}
          currentMonthIndex={this.currentMonthIndex}
          onMonthIndexChange={this.changeMonthIndex}
        />

        <div class={OS.desktop ? styles.currentDayBar : styles.currentDayBarMobile}>
          <span class={styles.dayOfWeek}>
            {weekdayName}
          </span>
          <span>&nbsp;{ordinalDay}</span>
          {
            this.currentDay && this.currentDay.author
              ? <span>
                  &nbsp;{this.localization.get("by")}
                  &nbsp;{this.currentDay.author}
                </span>
                : null
          }
        </div>

        <CalendarProgressPanel data={{
          completed: this.progress.completed,
          rate: this.progress.averageSolveRate,
          rateLabel: this.localization.get("Solve rate"),
          time: this.progress.averageTime,
          timeLabel: this.localization.get("Average solve time"),
        }} />

        <div class={styles.calendarContainer} ref="calendarContainer">
          {this.loading
            ? <div class={styles.loaderWrapper}>
                <div class={styles.loader}></div>
              </div>
            : <div class={styles.calendar}>
              {
                [
                  "MON",
                  "TUE",
                  "WED",
                  "THU",
                  "FRI",
                  "SAT",
                  "SUN",
                ].map((weekDay) => (
                  <div class={styles.dayHeader}>
                    {this.localization.get(weekDay)}
                  </div>
                ))
              }
              {this.dayEntries.map((item, index) => (
                <CalendarDay
                  onDayClicked={() => this.changeDayIndex(index)}
                  selected={index === this.selectedDayIndex}
                  options={item}
                  currentPuzzleId={this.puzzleId}
                  key={index}
                />
              ))}
            </div>
          }
        </div>

        <PopupButtons buttons={[{
          label: this.localization.get("Play"),
          onClick: () => sdkHandler.trigger('interstitialAd', { callback: function() {
            this.playHandler();
          }}, this),
        }]} />
      </PopupContainer>
    );
  },
});
